import { Badge, Flex, SegmentedControl, SegmentedControlItem, Text } from "@mantine/core";
import { IButtonWithBadge, ITaskCompleteCount } from "@mctypes/frontend.types";
import { IssueTabsEnum } from "../../../issueConstants";

type ButtonGroupWithBadgeProps = {
  buttons: IButtonWithBadge[];
  activeButtonName: IssueTabsEnum;
  setSelectedButtonName: (name: IssueTabsEnum) => void;
  style?: React.CSSProperties;
};

const ButtonGroupWithBadge: React.FC<ButtonGroupWithBadgeProps> = ({
  buttons,
  activeButtonName,
  setSelectedButtonName,
}) => {
  const data: SegmentedControlItem[] = buttons.map((button) => ({
    value: button.value,
    label: (
      <Flex align="center" justify="center" gap="xs" w="100%">
        {button.name}
        <BadgeIcon button={button} active={activeButtonName === button.name} />
        {button.taskCompleteCount && (
          <TaskCompletedCount taskCompleteCount={button.taskCompleteCount} active={activeButtonName === button.name} />
        )}
      </Flex>
    ),
  }));

  return (
    <SegmentedControl
      color="mc-primary"
      w="100%"
      data={data}
      value={activeButtonName}
      onChange={setSelectedButtonName}
    />
  );
};

type BadgeIconProps = {
  button: IButtonWithBadge;
  active: boolean;
};

export const BadgeIcon: React.FC<BadgeIconProps> = ({ button, active }) => {
  if (button.badgeIcon == null && button.badgeCount == null) {
    return null;
  }

  return (
    <Badge
      style={{ cursor: "pointer" }}
      h="20px"
      variant={active ? "filled" : "outline"}
      color={active ? "blue" : "gray"}
      radius="sm"
      fz={11}
    >
      {button.badgeIcon && (
        <i className={`fa ${button.badgeIcon}`} style={{ marginRight: button.badgeCount != null ? 4 : 0 }} />
      )}
      {button.badgeCount != null && <span>{button.badgeCount}</span>}
    </Badge>
  );
};

type TaskCompletedCountProps = {
  taskCompleteCount: ITaskCompleteCount;
  active?: boolean;
  useActionIcon?: boolean;
};

const TaskCompletedCount: React.FC<TaskCompletedCountProps> = ({ taskCompleteCount, active }) => {
  if (!taskCompleteCount || taskCompleteCount.totalNr <= 0) {
    return null;
  }

  const { completedCount, totalNr, showWarning } = taskCompleteCount;

  return (
    <Badge
      style={{ cursor: "pointer" }}
      h="20px"
      variant={active ? "filled" : "outline"}
      color={active ? "blue" : "gray"}
      sx={{ display: "flex", alignItems: "center" }}
    >
      <Flex align="center" gap={0}>
        {showWarning && <i className="fa fa-warning" style={{ color: "var(--mantine-color-red-6)" }} />}

        <CheckIcon completedNr={completedCount} totalNr={totalNr} />

        <Text component="span" fz={11}>
          {completedCount}/{totalNr}
        </Text>
      </Flex>
    </Badge>
  );
};

const CheckIcon: React.FC<{ completedNr: number; totalNr: number }> = ({ completedNr, totalNr }) => {
  const isComplete = totalNr > 0 && completedNr === totalNr;
  return <i className={`fa fa-check-circle fa-fw ${isComplete ? "icon-success" : ""}`} />;
};

export default ButtonGroupWithBadge;
